import React, {Fragment, type ReactNode, useState} from 'react';

import {useSelector} from 'react-redux';

import {CurrentUserSelector} from 'app/current-user/api';
import {AttachTooltip} from 'app/general/overlays';
import {CollapsableButtonArea} from 'app/general/structures/CollapsableButtonArea';
import {StatelessSimpleItemPickerList as StatelessSimpleItemPickerListRTK} from 'app/general/widgets/SimpleItemPickerList/SimpleItemPickerListRTK';
import {useTeams} from 'app/teams/api';
import {CreateTeamForm} from 'app/teams/components/CreateTeamWidget';
import {gt} from 'app/utils/i18n';

import {convertLocationParamsToFetchParams} from '../utils';

import type {TeamSerializedType} from 'sweet-types';

type DefaultProps = {
  small: boolean;
  isPopover?: boolean;
  disabledText?: (team: TeamSerializedType) => string | undefined;
};
type Props = DefaultProps & {
  onChange: (arg0: Array<TeamSerializedType>) => any;
  selected: Array<TeamSerializedType>;
  counterPhrase: (arg0: number) => HTMLElement | string | null;
  children?: ReactNode;
  onToggleCollapse?: (arg0: boolean) => void;
};

const readOnlyTeamText = (team: TeamSerializedType): string | undefined =>
  team.can_create
    ? undefined
    : gt('Cannot add to this team. You have read-only permission.');

export const CollapsableTeamPickerForm = (props: Props) => {
  const {
    onChange,
    small = false,
    children,
    selected,
    isPopover = false,
    disabledText,
    onToggleCollapse,
    counterPhrase,
  } = props;

  const [createTeamKey, setCreateTeamKey] = useState(1);
  const {
    id: userId,
    is_super_manager: isSuperManager,
    is_super_teammate: isSuperTeammate,
  } = useSelector((state) => CurrentUserSelector(state).data!);
  const isSuper = isSuperManager || isSuperTeammate;

  const getTeamsCounterPhrase = () => {
    return (
      <AttachTooltip placement="bottom" tooltipContent={getTeamsList()}>
        {(handlers) => (
          <span {...handlers}>{counterPhrase(selected.length)}</span>
        )}
      </AttachTooltip>
    );
  };

  const getTeamsList = () => (
    <ul className="text-left pl-4 mb-0">
      {props.selected.map((team) => (
        <li className="my-1" key={team.id}>
          {team.name}
        </li>
      ))}
    </ul>
  );
  const handleCreateTeam = (
    team: TeamSerializedType,
    refetchData: () => any
  ) => {
    onChange([...selected, team]);
    setCreateTeamKey(createTeamKey + 1);
    refetchData();
  };
  const getTeamProps = (team: TeamSerializedType): {disabledText?: string} => ({
    disabledText:
      disabledText != null ? disabledText(team) : readOnlyTeamText(team),
  });

  return (
    <Fragment>
      <CollapsableButtonArea
        buttonText={gt('Add to teams')}
        small={small}
        collapsed={isSuperManager}
        onToggleCollapse={onToggleCollapse}
      >
        <StatelessSimpleItemPickerListRTK
          additionalParams={
            !isSuper
              ? {
                  user_id: userId,
                }
              : undefined
          }
          selected={selected}
          showDescription={true}
          setSelected={onChange}
          paramsConverter={convertLocationParamsToFetchParams}
          useDataHook={useTeams}
          searchPlaceholder={gt('Filter teams')}
          isPopover={isPopover}
          isSmall={small}
          addItemProps={getTeamProps}
        >
          {(selected, refetchData) => (
            <Fragment>
              {children}
              {isSuperManager ? (
                <CollapsableButtonArea
                  buttonText={gt('Create Team')}
                  key={createTeamKey}
                  small={small}
                >
                  <CreateTeamForm
                    small={small}
                    showCancelButton={false}
                    isPopover={isPopover}
                    onCreateTeam={(team) => handleCreateTeam(team, refetchData)}
                  />
                </CollapsableButtonArea>
              ) : null}
            </Fragment>
          )}
        </StatelessSimpleItemPickerListRTK>
      </CollapsableButtonArea>
      {selected.length > 0 ? (
        <div className={`my-4 font-weight-bold ${small ? 'small' : ''}`}>
          {getTeamsCounterPhrase()}
        </div>
      ) : null}
    </Fragment>
  );
};
